import { Button, Drawer } from "@components";
import { useEffect, useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import { LifeCycleStagesChart } from "./LifeCycleStagesChart";
import { BubbleChart } from "pages/Reports/BubbleChart";
import { Numbers } from "shared/utils/Numbers";
import {
  getCurrentMonthYear,
  getLastSixYears,
  getPreviousMonth,
  months,
} from "pages/Reports/Reports";
import dayjs from "dayjs";
import { ReportsAPI } from "providers/ReportsAPI";

const getSecondToLast = () => {
  const today = new Date();

  const date = new Date(today.getFullYear(), today.getMonth() - 2);
  const mes = months[date.getMonth()];
  const ano = date.getFullYear();

  return {
    name: `${mes} ${ano}`,
    id: `${date.getMonth() + 1}`,
  };
};

const Trend = ({ growth, invertColorSign }) => {
  const upColor = invertColorSign ? "#e48186" : "#51D9A3";
  const downColor = invertColorSign ? "#51D9A3" : "#e48186";

  const sign = growth > 0 ? "+" : "";
  const isNegative = growth < 0;
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? downColor : upColor;

  return (
    <p
      className={styles.growth}
      style={{ color: iconColor }}
      title={`Variação percentual em relação à ${getSecondToLast().name}`}
    >
      {sign}
      {Numbers.number(growth, 2)}%{" "}
      <i className={iconClass} style={{ color: iconColor }} />
    </p>
  );
};

const calculatePercentage = (part, total) => {
  if (!total || isNaN(part) || isNaN(total)) return 0;
  return (part / total) * 100;
};

const groups = (data = []) => {
  const total_customers = data?.statistics?.total_customers?.value || 0;

  return [
    {
      alias: "CLIENTES_NOVOS",
      name: "CLIENTES NOVOS",
      borderColor: "#e1ffb4",
      valueColor: "#3c4035",
      bgColor: "#252623",
      tooltip: "Clientes que compraram recentemente e tem apenas 1 pedido.",
      total: data?.novos?.value || 0,
      growth: data?.novos?.growth || 0,
      percentage: calculatePercentage(data?.novos?.value, total_customers),
    },
    {
      alias: "CLIENTES_EM_ONBOARDING",
      name: "CLIENTES EM ONBOARDING",
      borderColor: "#6af3ee",
      valueColor: "#2a3e3e",
      bgColor: "#212525",
      tooltip: "Clientes que compraram recentemente e tem 2 ou 3 pedidos.",
      total: data?.onboarding?.value || 0,
      growth: data?.onboarding?.growth || 0,
      percentage: calculatePercentage(data?.onboarding?.value, total_customers),
    },
    {
      alias: "CLIENTES_ENGAJADOS",
      name: "CLIENTES ENGAJADOS",
      borderColor: "#a67666",
      valueColor: "#332c29",
      bgColor: "#232221",
      tooltip: "Clientes que compraram recentemente e tem 4 ou mais pedidos.",
      total: data?.engajados?.value || 0,
      growth: data?.engajados?.growth || 0,
      percentage: calculatePercentage(data?.engajados?.value, total_customers),
    },
    {
      alias: "CLIENTES_ATRASADOS",
      name: "CLIENTES ATRASADOS",
      borderColor: "#E48186",
      valueColor: "#3a2b2c",
      bgColor: "#221f1f",
      tooltip: "Clientes que não compram há muito tempo.",
      total: data?.atrasados?.value || 0,
      growth: data?.atrasados?.growth || 0,
      percentage: calculatePercentage(data?.atrasados?.value, total_customers),
    },
  ];
};

export default function LifeCycleStages({ data }) {
  const [selectedData, setSelectedData] = useState(null);
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [tempData, setTempData] = useState(null);

  const getLastValidItem = (data) => {
    for (let i = data.length - 1; i >= 0; i--) {
      const item = data[i];
      if (item && typeof item === "object") {
        return item;
      }
    }

    return null;
  };

  const fetchHistory = async (payload) => {
    setLoadingHistoric(true);
    const { year } = payload;

    const provider = new ReportsAPI();
    const results =
      (await provider.lifeCycleHistory("stages", year, null)) || [];

    const limit = 12;
    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      if (!result?.month) continue;

      const format = "MM/YYYY";
      const date = dayjs(result.month, format);

      const index = date.month() + 1;

      if (index >= 1 && index <= limit) {
        rows[index - 1] = Object.fromEntries(
          Object.entries(result)
            .map(([key, obj]) => {
              if (key === "month") return null;
              return [
                key,
                {
                  value: Number(obj?.value ?? 0),
                  growth: Number(obj?.growth ?? 0),
                },
              ];
            })
            .filter(Boolean),
        );
      }
    }

    setLoadingHistoric(false);

    const result = getLastValidItem(rows);

    setTempData(result);
    return rows;
  };

  useEffect(() => {
    const years = getLastSixYears(true);
    fetchHistory({ year: years[0] });
  }, []);

  return (
    <div className={styles.box2}>
      <div className="d-flex align-center justify-between px-15">
        <div className="d-flex align-center relative gap-5">
          <p className={styles.label}>Estágios do ciclo de vida</p>
          <span
            className={styles.info}
            title="Quantidade de clientes para cada estágio do ciclo de vida."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <div className="d-flex align-center">
          <span className={styles.currentMonth}>{getPreviousMonth().name}</span>
          <div className={styles.divider} />
          <Button
            design="default-icon-mini-3"
            onClick={() =>
              setSelectedData({
                name: "Estágios do ciclo de vida",
              })
            }
          >
            <i className="fas fa-chart-waterfall" />
          </Button>
        </div>
      </div>

      <div className={styles.hairline} />

      <div className="px-15">
        <div className={styles.container}>
          <div className={styles.customers}>
            <div className={styles.grid2}>
              {groups(data).map((group, index) => {
                const total = groups(data).reduce(
                  (acc, item) => acc + Number(item.total),
                  0,
                );

                const percent = groups(data).map((item) => {
                  const percentual = (Number(item.total) / total) * 100;
                  return percentual.toFixed(2);
                });

                const percentNumber = parseFloat(
                  Numbers.number(percent[index]).replace(",", "."),
                );

                return (
                  <div className={styles.category} key={group.alias}>
                    <div
                      className={styles.border}
                      style={{
                        backgroundColor: group.borderColor || "#fff",
                      }}
                    />
                    <div className="d-flex flex-column w-100">
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {(+group.total || 0).toLocaleString("pt-BR")}
                        </p>
                        <div className="d-flex align-center">
                          <Trend
                            growth={group.growth}
                            invertColorSign={
                              group.alias === "CLIENTES_ATRASADOS"
                            }
                          />
                        </div>
                      </div>
                      <div className="d-flex align-center w-100 justify-between">
                        <div className="d-flex align-center gap-5">
                          <p className={styles.name}>{group.name}</p>
                          <span className={styles.info} title={group.tooltip}>
                            <i className="fal fa-info-circle" />
                          </span>
                        </div>
                        <p
                          className={styles.percent}
                          style={{
                            backgroundColor: group.valueColor || "#fff",
                          }}
                        >
                          {Numbers.number(percent[index])}%
                        </p>
                      </div>
                    </div>
                    <div
                      className={styles.percentage}
                      style={{
                        width: `${percentNumber}%`,
                        backgroundColor: group.bgColor || "#fff",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.bubbleChart}>
            <span className={styles.circleA} />
            <span className={styles.circleB} />
            <span className={styles.circleC} />
            <div
              style={{
                width: "100%",
                height: 300,
              }}
            >
              <BubbleChart
                data={{
                  name: "root",
                  children: groups(data).map((group) => ({
                    name: group.name,
                    value: group.total,
                    color: group.borderColor,
                  })),
                }}
                height={300}
              />
            </div>
          </div>
        </div>
      </div>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <LifeCycleStagesChart
          selectedData={selectedData}
          onFetchData={fetchHistory}
          loading={loadingHistoric}
          onClose={() => setSelectedData(false)}
          startOn2025
        />
      </Drawer>
    </div>
  );
}
